import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import appConfig from 'config/app.config';
import {getText, renderMarkdown} from 'helpers/text-helper';
import Button from 'components/ui/button/button';
import './login-group.scss';

const LoginGroup = (props) => {
	const {
		languageId, 
		deviceInfo, 
		createGroup, 
		getLoginToken, 
		loginWithToken,
		calculateFontSize
	} = props;
	
	/* Loading status and error message */
	const [isFindingGame, setIsFindingGame] = useState(true);
	const [showWelcomePage, setShowWelcomePage] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [errMsg, setErrMsg] = useState(null);
	
	/* Game groups */
	const [gameData, setGameData] = useState(null);
	const [gameGroups, setGameGroups] = useState(null);
	const [selectedGroupId, setSelectedGroupId] = useState(null);

	/* Show create group, group name input */
	const [showCreateGroup, setShowCreateGroup] = useState(false);
	const [groupName, setGroupName] = useState(null);


	const handleStart = () => {
		setShowWelcomePage(false);
		calculateFontSize();
	};
	

	/**
	 * Find the game based on the url
	 * @param {string} gameCode 
	 */
	const findGame = (gameCode) => {
		return new Promise((resolve) => {
			const db = firebase.firestore();
			/* Get game */
			db.collection(appConfig.gamesDbName).doc(gameCode).get().then((doc) => {
				if (doc.exists && doc.data()) {
					/* Game */
					const game = {...doc.data(), id: doc.id};

					/* Get groups of game */
					db.collection(appConfig.groupsDbName).where('gameId', '==', doc.id).get().then((docs) => {
						const groups = [];

						docs.forEach((doc) => {groups.push({id: doc.id, ...doc.data()});});
						resolve({status: 'success', game, groups});
					}).catch((error) => {
						resolve({status: 'error', error: error});	
					});
				} else {
					resolve({status: 'error'});
				}
			}).catch((error) => {
				resolve({status: 'error', error: error});	
			});	
		});	
	};

	/**
	 * Get login token for existing group and log in
	 * @param {string} groupId 
	 */
	const handleGetLoginToken = (groupId) => {
		setIsLoading(true);
		setSelectedGroupId(groupId);
		getLoginToken(gameData.id, groupId).then((response) => {
			if (response.status === 'success') {
				loginWithToken(response.token).catch((error) => {
					console.error(error);
					setErrMsg(getText('errors_unknownError', languageId));
					setShowCreateGroup(false);
					setIsLoading(false);
					setSelectedGroupId(null);
				});
			} else {
				console.error(response.error);
				setErrMsg(getText('errors_unknownError', languageId));
				setShowCreateGroup(false);
				setIsLoading(false);
				setSelectedGroupId(null);
			}
		});
	};

	/**
	 * Handle group name input
	 * @param {object} event 
	 */
	const handleGroupNameInput = (event) => {
		setErrMsg(null);
		setGroupName(event.target.value);
	};

	/**
	 * Create new group and log in
	 */
	const handleCreateGroup = (event) => {
		if (event) event.preventDefault();

		if (isLoading) return;
		
		if (!groupName || groupName.length < appConfig.groupNameMinLength) {
			/* Group name too short */
			const newErrMsg = JSON.parse(JSON.stringify(getText('errors_groupNameTooShort', languageId)))
				.replace('%minChars%', appConfig.groupNameMinLength);
			setErrMsg(newErrMsg);
		} else if (groupName.length > appConfig.groupNameMaxLength) {
			/* Group name too long */
			const newErrMsg = JSON.parse(JSON.stringify(getText('errors_groupNameTooLong', languageId)))
				.replace('%maxChars%', appConfig.groupNameMaxLength);
			setErrMsg(newErrMsg);
		} else {
			setIsLoading(true);
			createGroup(gameData.id, gameData.themeId, groupName).then((response) => {
				if (response.status === 'success') {
					loginWithToken(response.token).catch((error) => {
						console.error(error);
						setErrMsg(getText('errors_unknownError', languageId));
						setShowCreateGroup(false);
						setIsLoading(false);
					});
				} else {
					let newErrMsg = getText('errors_unknownError', languageId);
					if (response.error === 'group-name-already-exists') {
						newErrMsg = getText('errors_groupAlreadyExists', languageId);
					}
					setErrMsg(newErrMsg);
					setIsLoading(false);
				}
			});
		}
	};

	/**
	 * Component mounted / will unmount
	 */
	useEffect(() => {
		/* Component mounted */ 
		const pathName = window.location.pathname.toLowerCase();
		if (pathName.length > 0) {
			const pathNameArr = pathName.split('/');
			if (pathNameArr.length === 3 && pathNameArr[1] === 'gruppe' && pathNameArr[2].length > 0) {
				const gameCode = pathNameArr[2];
				findGame(gameCode).then((response) => {
					if (response.status === 'success') {
						setGameData(response.game);
						setGameGroups(response.groups);
					}
					setIsFindingGame(false);
				});

			} else {
				setIsFindingGame(false);
			}
		} else {
			setIsFindingGame(false);
		}
	}, []);

	/* Is finding game from url */
	if (isFindingGame) {
		return (
			<div className='LoginGroup loading'>
				<span>{getText('general_loadingGame', languageId)}</span>
			</div>
		);
	}

	if (gameData && gameGroups) {
		/* Show welcome page */
		if (showWelcomePage) {
			return (
				<div className={'LoginGroup welcome ' + deviceInfo.orientation}>
					<form className='LoginGroup-form'>
						<div className='LoginGroup-title'>
							<span>{getText('group_welcomeTitle', languageId)}</span>
						</div>
						<div className='LoginGroup-text'>
							{renderMarkdown(getText('group_welcomeText', languageId))}
						</div>
						<div className="LoginGroup-startBtn">
							<Button
								isLoading={isLoading}
								text={getText('general_start', languageId)}
								classes={['orange', 'start', 'fullHeight']}
								onClick={() => {handleStart();}}
							/>
						</div>
					</form>
					<div className="LoginGroup-groupIcon" />
				</div>
			);
		}


		/* Create new group */
		if (showCreateGroup || (gameGroups !== null && gameGroups.length === 0)) {
			return (
				<div className={'LoginGroup create ' + deviceInfo.orientation}>
					<form className='LoginGroup-form' onSubmit={handleCreateGroup}>
						<div className='LoginGroup-title'>
							<span>{getText('general_newGroup', languageId)}</span>
						</div>
						<div className='LoginGroup-image'/>
						<input
							className='LoginGroup-input name'
							name="groupName"
							type="text"
							placeholder={getText('general_writeGroupName', languageId)} 
							autoComplete="section-grouplogin groupname"
							value={groupName ? groupName : ''} 
							onChange={(event)=>{handleGroupNameInput(event);}}
						/>
						<div className='LoginGroup-button'>
							<Button
								isLoading={isLoading}
								type="submit"
								text={getText('general_createGroup', languageId)}
								classes={['orange', 'fullHeight']}
							/>
						</div>
						<div className='LoginGroup-errMsg'>
							<span>{errMsg}</span>
						</div>
					</form>
					<div className="LoginGroup-groupIcon" />
				</div>
			);
		}

		/* Select group to log in as */
		return (
			<div className={'LoginGroup groups ' + deviceInfo.orientation}>
				<div className='LoginGroup-form'>
					<div className='LoginGroup-title'>
						<span>{getText('general_selectAGroup', languageId)}</span>
					</div>
					<div className='LoginGroup-newGroupBtn' onClick={() => {setShowCreateGroup(true);}}>
						<span>{getText('general_createGroup', languageId)}</span>
					</div>
					<div className='LoginGroup-groups'>
						{gameGroups.map((group) => {
							return (
								<div 
									key={group.id}
									onClick={() => {handleGetLoginToken(group.id);}} 
									className={'LoginGroup-group' 
									+ (isLoading && selectedGroupId === group.id ? ' loading' : '')}
								>
									<span></span>
									<span>{group.name}</span>
								</div>
							);
						})}
					</div>
					<div className='LoginGroup-errMsg'>
						<span>{errMsg}</span>
					</div>
				</div>
				<div className="LoginGroup-groupIcon" />
			</div>
		);
	}


	/* No game found at this urls*/
	if (!gameData || !gameGroups) {
		return (
			<div className={'LoginGroup noGame ' + deviceInfo.orientation}>
				<div className='LoginGroup-form'>
					<div className="LoginGroup-noGame">
						<span>{getText('errors_gameDoesNotExist', languageId)}</span>
					</div>
				</div>
				<div className="LoginGroup-groupIcon" />
			</div>
		);
	}
};

LoginGroup.propTypes = {
	languageId: PropTypes.string.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	createGroup: PropTypes.func.isRequired,
	getLoginToken: PropTypes.func.isRequired,
	loginWithToken: PropTypes.func.isRequired,
	calculateFontSize: PropTypes.func.isRequired
};

export default LoginGroup;